import React, { useState, useEffect, useRef, useMemo } from "react"
import {
  CalendarIcon,
  XMarkIcon,
  ChevronDownIcon,
  CheckIcon,
} from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import DatePicker from "aldoo-ra/DatePicker"
import VariantReader from "aldoo-ra/VariantReader"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { twMerge } from "tailwind-merge"
import moment from "moment"

/**
 * DateFilterDropdown component for filtering data by date ranges
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Callback function when date filter changes
 * @param {Object} props.value - Current filter value (controlled component)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disableLifetime - Option to disable lifetime filter
 * @param {Array} props.customPresets - Custom preset options
 * @param {string} props.variant - UI variant to use
 * @param {Object} props.styleOverrides - Override styles for the component
 * @param {string} props.label - Label for the dropdown
 * @returns {JSX.Element} The DateFilterDropdown component
 */
const DateFilterDropdown = ({
  onChange,
  value,
  className,
  disableLifetime = false,
  customPresets = null,
  variant = "default",
  styleOverrides = {},
  label = "Date Range",
  showLabel = true,
  highlightOnSelection = false,
  highlightResetValue = "lifetime",
  localUserPreferencesKey = null, // Key for storing user preferences
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState(() => {
    // Try to load from user preferences first, then fall back to "lifetime"
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        const savedPreset = LocalUserPreferences.get(`${localUserPreferencesKey}.preset`)
        if (savedPreset !== undefined && typeof savedPreset === 'string') {
          return savedPreset
        }
      } catch (error) {
        console.warn("Failed to load date filter preset preference:", error)
      }
    }
    return "lifetime"
  })
  const [customRange, setCustomRange] = useState(() => {
    // Try to load from user preferences first, then fall back to null
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        const savedRange = LocalUserPreferences.get(`${localUserPreferencesKey}.customRange`)
        if (savedRange !== undefined && savedRange !== null) {
          return savedRange
        }
      } catch (error) {
        console.warn("Failed to load date filter custom range preference:", error)
      }
    }
    return null
  })
  const dropdownRef = useRef(null)

  // Get styles from variant system
  const variants = VariantReader.useVariant({
    componentName: "DateFilterDropdown",
    variant,
    styleOverrides,
  })

  // Memoize presets to ensure they don't change on every render
  const presets = useMemo(() => {
    const defaultPresets = [
      { id: "last24h", label: "Last 24 Hours", value: { days: 1 } },
      { id: "last7d", label: "Last 7 Days", value: { days: 7 } },
      { id: "last30d", label: "Last 30 Days", value: { days: 30 } },
      { id: "last90d", label: "Last 90 Days", value: { days: 90 } },
      { id: "last365d", label: "Last 365 Days", value: { days: 365 } },
      ...(disableLifetime
        ? []
        : [{ id: "lifetime", label: "Lifetime", value: null }]),
      { id: "custom", label: "Custom Range", value: "custom" },
    ]

    return customPresets || defaultPresets
  }, [customPresets, disableLifetime])

  // Sync with external value prop when it changes
  useEffect(() => {
    if (value) {
      if (value.type === "custom" && value.range) {
        // For custom date range
        setCustomRange(value.range)
        setSelectedPreset("custom")
      } else if (value.type === "preset") {
        // For preset values
        if (value.value === null) {
          // Lifetime
          setSelectedPreset("lifetime")
        } else {
          // Find matching preset by days
          const days = value.value?.days
          if (days) {
            const match = presets.find((p) => p.value?.days === days)
            if (match) {
              setSelectedPreset(match.id)
            }
          }
        }
      }
    }
  }, [value, presets])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Handle preset selection
  const handlePresetSelect = (presetId) => {
    setSelectedPreset(presetId)
    
    // Save to local user preferences if key is provided
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        LocalUserPreferences.set(`${localUserPreferencesKey}.preset`, presetId)
      } catch (error) {
        console.warn("Failed to save date filter preset preference:", error)
      }
    }

    // Call onChange with appropriate value
    if (presetId === "custom") {
      // Only update if we have a valid custom range
      if (customRange) {
        onChange({ type: "custom", range: customRange })
      }
    } else {
      const preset = presets.find((p) => p.id === presetId)
      if (preset) {
        onChange({ type: "preset", value: preset.value })
      }
    }

    if (presetId !== "custom") {
      setIsOpen(false)
    }
  }

  // Handle custom range selection
  const handleCustomRangeSelect = (range) => {
    setCustomRange(range)
    
    // Save to local user preferences if key is provided
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        LocalUserPreferences.set(`${localUserPreferencesKey}.customRange`, range)
      } catch (error) {
        console.warn("Failed to save date filter custom range preference:", error)
      }
    }

    // Call onChange with the custom date range
    if (range && range.startDate && range.endDate) {
      onChange({ type: "custom", range })
    }

    setSelectedPreset("custom")
  }

  // Get display label for current selection
  // Helper function to determine if selection should be highlighted
  const shouldHighlight = useMemo(() => {
    if (!highlightOnSelection) return false
    return selectedPreset !== highlightResetValue
  }, [highlightOnSelection, selectedPreset, highlightResetValue])

  const getDisplayLabel = () => {
    if (selectedPreset === "custom" && customRange) {
      const { startDate, endDate } = customRange
      const start = moment(startDate).format("MM/DD/YYYY")
      const end = moment(endDate).format("MM/DD/YYYY")
      return `${start} - ${end}`
    }

    const preset = presets.find((p) => p.id === selectedPreset)
    return preset ? preset.label : "Select Date Range"
  }

  return (
    <div className={twMerge(variants.root, className)} ref={dropdownRef}>
      <div className={variants.wrapper}>
        {showLabel && (
          <Typography variant="admin_txt_accent" className={variants.label}>
            {label}
          </Typography>
        )}

        <div
          className={twMerge(
            variants.dropdown_button,
            shouldHighlight && variants.highlight
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center">
            <CalendarIcon className={variants.dropdown_icon} />
            <Typography variant="admin_txt1" className={variants.dropdown_text}>
              {getDisplayLabel()}
            </Typography>
          </div>
          <ChevronDownIcon className={variants.chevron_icon} />
        </div>
      </div>

      {isOpen && (
        <div className={variants.popup}>
          {/* Preset options */}
          <div className={variants.popup_scroll}>
            {presets.map((preset) => (
              <div
                key={preset.id}
                className={twMerge(
                  variants.preset_item.base,
                  selectedPreset === preset.id
                    ? variants.preset_item.active
                    : variants.preset_item.hover
                )}
                onClick={() => handlePresetSelect(preset.id)}
              >
                <Typography variant="admin_txt1" className="my-0.5">
                  {preset.label}
                </Typography>
                {selectedPreset === preset.id && (
                  <CheckIcon className={variants.preset_check_icon} />
                )}
              </div>
            ))}
          </div>

          {/* Custom date range picker */}
          {selectedPreset === "custom" && (
            <div className={variants.custom_section}>
              <DatePicker
                range={true}
                value={customRange}
                onChange={handleCustomRangeSelect}
                dateFormat="MM/DD/YYYY"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DateFilterDropdown
